import type { ComputedRef, Ref } from 'vue';
import {
    resolveExportDimensions,
    resolveWatermarkCenter,
    type CanvasImageExportFormat,
    type ImageEditorCropMode,
    type ImageEditorDimensions
} from './imageEditor';
import type {
    CropperTemplate,
    CropSelection,
    WatermarkType
} from './cropper';

interface CropperCanvasActionsContext
{
    canvas: ComputedRef<HTMLCanvasElement | null>;
    previewCanvas: ComputedRef<HTMLCanvasElement | null>;
    image: Ref<HTMLImageElement | null>;
    watermarkLogo: Ref<HTMLImageElement | null>;
    canvasWidth: Ref<number>;
    canvasHeight: Ref<number>;
    viewportZoom: Ref<number>;
    cropSelection: CropSelection;
    cropMode: Ref<ImageEditorCropMode>;
    aspectRatio: Ref<string>;
    fixedSize: ImageEditorDimensions;
    circularSize: Ref<number>;
    templateSize: Ref<ImageEditorDimensions | null>;
    rotation: Ref<number>;
    flipHorizontal: Ref<boolean>;
    flipVertical: Ref<boolean>;
    scale: Ref<number>;
    filterString: ComputedRef<string>;
    watermarkEnabled: Ref<boolean>;
    watermarkType: Ref<WatermarkType>;
    watermarkText: Ref<string>;
    watermarkX: Ref<number>;
    watermarkY: Ref<number>;
    watermarkOpacity: Ref<number>;
    watermarkSize: Ref<number>;
    exportFormat: Ref<CanvasImageExportFormat> | ComputedRef<CanvasImageExportFormat>;
    outputResizeEnabled: Ref<boolean>;
    outputSize: ImageEditorDimensions;
    optimizeDimensions: Ref<boolean>;
    maxDimension: Ref<number>;
    saveHistory: (label: string) => void;
    translate: (key: string) => string;
}

function drawWatermark(context: CropperCanvasActionsContext, renderingContext: CanvasRenderingContext2D, width: number, height: number)
{
    if (!context.watermarkEnabled.value)
        return;

    renderingContext.save();
    renderingContext.globalAlpha = context.watermarkOpacity.value / 100;

    if (context.watermarkType.value === 'logo')
    {
        if (!context.watermarkLogo.value)
        {
            renderingContext.restore();

            return;
        }

        const logoWidth = Math.min(width * 0.25, context.watermarkSize.value * 4);
        const logoHeight = logoWidth * (context.watermarkLogo.value.height / context.watermarkLogo.value.width);
        const { x, y } = resolveWatermarkCenter(width, height, context.watermarkX.value, context.watermarkY.value, logoWidth, logoHeight);

        renderingContext.drawImage(context.watermarkLogo.value, x - logoWidth / 2, y - logoHeight / 2, logoWidth, logoHeight);
    }
    else
    {
        renderingContext.font = `700 ${context.watermarkSize.value}px Arial`;
        renderingContext.textAlign = 'center';
        renderingContext.textBaseline = 'middle';
        renderingContext.lineWidth = 4;
        renderingContext.strokeStyle = 'rgba(0, 0, 0, 0.55)';
        renderingContext.fillStyle = '#fff';

        const metrics = renderingContext.measureText(context.watermarkText.value);
        const textWidth = metrics.width + renderingContext.lineWidth;
        const textHeight = (metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || context.watermarkSize.value) + renderingContext.lineWidth;
        const { x, y } = resolveWatermarkCenter(width, height, context.watermarkX.value, context.watermarkY.value, textWidth, textHeight);

        renderingContext.strokeText(context.watermarkText.value, x, y);
        renderingContext.fillText(context.watermarkText.value, x, y);
    }

    renderingContext.restore();
}

export function createCropperCanvasActions(context: CropperCanvasActionsContext)
{
    function canvasRect()
    {
        return context.canvas.value.getBoundingClientRect();
    }

    function pointFromEvent(event: MouseEvent)
    {
        const rect = canvasRect();
        const ratioX = context.canvas.value.width / rect.width;
        const ratioY = context.canvas.value.height / rect.height;

        return {
            x: Math.max(0, Math.min(context.canvas.value.width, (event.clientX - rect.left) * ratioX)),
            y: Math.max(0, Math.min(context.canvas.value.height, (event.clientY - rect.top) * ratioY))
        };
    }

    function getRatio()
    {
        if (context.cropMode.value === 'fixed-ratio')
        {
            return parseFloat(context.aspectRatio.value);
        }

        if (context.cropMode.value === 'fixed-size')
        {
            return context.fixedSize.width / context.fixedSize.height;
        }

        if (context.cropMode.value === 'template' && context.templateSize.value)
        {
            return context.templateSize.value.width / context.templateSize.value.height;
        }

        if (context.cropMode.value === 'circular')
        {
            return 1;
        }

        return 0;
    }

    function setViewportZoom(value: number)
    {
        context.viewportZoom.value = Math.max(25, Math.min(300, value));
    }

    function applySelection(x: number, y: number, width: number, height: number)
    {
        const ratio = getRatio();

        if (ratio > 0)
        {
            if (Math.abs(width) / Math.max(Math.abs(height), 1) > ratio)
            {
                width = Math.sign(width || 1) * Math.abs(height) * ratio;
            }
            else
            {
                height = Math.sign(height || 1) * Math.abs(width) / ratio;
            }
        }

        const left = Math.max(0, Math.min(x, x + width));
        const top = Math.max(0, Math.min(y, y + height));
        const right = Math.min(context.canvas.value.width, Math.max(x, x + width));
        const bottom = Math.min(context.canvas.value.height, Math.max(y, y + height));

        context.cropSelection.x = Math.round(left);
        context.cropSelection.y = Math.round(top);
        context.cropSelection.width = Math.max(1, Math.round(right - left));
        context.cropSelection.height = Math.max(1, Math.round(bottom - top));
        context.cropSelection.active = true;

        updatePreview();
    }

    function setCenteredSelection(width: number, height: number)
    {
        if (!context.canvas.value)
            return;

        const safeWidth = Math.min(width, context.canvas.value.width);
        const safeHeight = Math.min(height, context.canvas.value.height);

        context.cropSelection.x = Math.round((context.canvas.value.width - safeWidth) / 2);
        context.cropSelection.y = Math.round((context.canvas.value.height - safeHeight) / 2);
        context.cropSelection.width = Math.round(safeWidth);
        context.cropSelection.height = Math.round(safeHeight);
        context.cropSelection.active = true;

        updatePreview();
    }

    function resetSelection()
    {
        if (!context.canvas.value)
            return;

        if (context.cropMode.value === 'none')
        {
            context.cropSelection.active = false;
            updatePreview();

            return;
        }

        const maxWidth = context.canvas.value.width * 0.7;
        const maxHeight = context.canvas.value.height * 0.7;
        const ratio = getRatio();

        if (ratio > 0)
        {
            // dopasuj prostokąt o zadanej proporcji do 70% obszaru, ograniczając przez krótszy bok
            if (maxWidth / maxHeight > ratio)
            {
                setCenteredSelection(maxHeight * ratio, maxHeight);
            }
            else
            {
                setCenteredSelection(maxWidth, maxWidth / ratio);
            }

            return;
        }

        setCenteredSelection(maxWidth, maxHeight);
    }

    function redrawCanvas()
    {
        if (!context.canvas.value || !context.image.value)
            return;

        const renderingContext = context.canvas.value.getContext('2d');

        if (!renderingContext)
            return;

        const scaledWidth = Math.round(context.image.value.width * (context.scale.value / 100));
        const scaledHeight = Math.round(context.image.value.height * (context.scale.value / 100));
        const rotated = context.rotation.value === 90 || context.rotation.value === 270;

        context.canvas.value.width = rotated ? scaledHeight : scaledWidth;
        context.canvas.value.height = rotated ? scaledWidth : scaledHeight;
        context.canvasWidth.value = context.canvas.value.width;
        context.canvasHeight.value = context.canvas.value.height;

        renderingContext.clearRect(0, 0, context.canvas.value.width, context.canvas.value.height);
        renderingContext.save();
        renderingContext.filter = context.filterString.value;
        renderingContext.translate(context.canvas.value.width / 2, context.canvas.value.height / 2);
        renderingContext.rotate((context.rotation.value * Math.PI) / 180);
        renderingContext.scale(context.flipHorizontal.value ? -1 : 1, context.flipVertical.value ? -1 : 1);
        renderingContext.drawImage(context.image.value, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        renderingContext.restore();

        if (context.cropMode.value === 'none')
        {
            context.cropSelection.active = false;
            updatePreview();
        }
        else if (context.cropSelection.active)
        {
            context.cropSelection.x = Math.min(context.cropSelection.x, context.canvas.value.width - 1);
            context.cropSelection.y = Math.min(context.cropSelection.y, context.canvas.value.height - 1);
            context.cropSelection.width = Math.min(context.cropSelection.width, context.canvas.value.width - context.cropSelection.x);
            context.cropSelection.height = Math.min(context.cropSelection.height, context.canvas.value.height - context.cropSelection.y);
            updatePreview();
        }
        else
        {
            resetSelection();
        }
    }

    function updatePreview()
    {
        if (!context.previewCanvas.value || !context.canvas.value)
            return;

        if (context.cropMode.value !== 'none' && !context.cropSelection.active)
            return;

        const renderingContext = context.previewCanvas.value.getContext('2d');

        if (!renderingContext)
            return;

        const cropSource = context.cropMode.value === 'none'
            ? {
                x: 0,
                y: 0,
                width: context.canvas.value.width,
                height: context.canvas.value.height
            }
            : {
                x: context.cropSelection.x,
                y: context.cropSelection.y,
                width: context.cropSelection.width,
                height: context.cropSelection.height
            };
        const dimensions = resolveExportDimensions({
            cropWidth: cropSource.width,
            cropHeight: cropSource.height,
            mode: context.cropMode.value,
            fixedSize: context.fixedSize,
            circularSize: context.circularSize.value,
            templateSize: context.templateSize.value,
            outputResizeEnabled: context.outputResizeEnabled.value,
            outputSize: context.outputSize,
            optimizeDimensions: context.optimizeDimensions.value,
            maxDimension: context.maxDimension.value
        });

        context.previewCanvas.value.width = dimensions.width;
        context.previewCanvas.value.height = dimensions.height;

        if (context.exportFormat.value === 'jpeg')
        {
            renderingContext.fillStyle = '#fff';
            renderingContext.fillRect(0, 0, dimensions.width, dimensions.height);
        }
        else
        {
            renderingContext.clearRect(0, 0, dimensions.width, dimensions.height);
        }

        if (context.cropMode.value === 'circular')
        {
            renderingContext.save();
            renderingContext.beginPath();
            renderingContext.arc(dimensions.width / 2, dimensions.height / 2, Math.min(dimensions.width, dimensions.height) / 2, 0, Math.PI * 2);
            renderingContext.clip();
        }

        renderingContext.drawImage(
            context.canvas.value,
            cropSource.x,
            cropSource.y,
            cropSource.width,
            cropSource.height,
            0,
            0,
            dimensions.width,
            dimensions.height
        );

        if (context.cropMode.value === 'circular')
        {
            renderingContext.restore();
        }

        drawWatermark(context, renderingContext, dimensions.width, dimensions.height);
    }

    function setCropMode(value: ImageEditorCropMode)
    {
        context.cropMode.value = value;

        if (value === 'none')
        {
            context.cropSelection.active = false;
            updatePreview();
        }
        else if (value === 'fixed-size')
        {
            setCenteredSelection(context.fixedSize.width, context.fixedSize.height);
        }
        else if (value === 'circular')
        {
            setCenteredSelection(context.circularSize.value, context.circularSize.value);
        }
        else if (value === 'template' && context.templateSize.value)
        {
            setCenteredSelection(context.templateSize.value.width, context.templateSize.value.height);
        }
        else
        {
            resetSelection();
        }

        context.saveHistory(context.translate('[[[Zmiana trybu kadrowania]]]'));
    }

    function applyTemplate(template: CropperTemplate)
    {
        context.templateSize.value = {
            width: template.width,
            height: template.height
        };
        context.cropMode.value = 'template';
        setCenteredSelection(template.width, template.height);
        context.saveHistory(template.name);
    }

    function applyTransform(label: string)
    {
        redrawCanvas();
        context.saveHistory(label);
    }

    function rotateImage(degrees: number)
    {
        context.rotation.value = (context.rotation.value + degrees) % 360;

        if (context.rotation.value < 0)
        {
            context.rotation.value += 360;
        }

        applyTransform(context.translate('[[[Obrót obrazu]]]'));
    }

    function nudgeSelection(dx: number, dy: number, resize = false)
    {
        if (!context.canvas.value || context.cropMode.value === 'none' || !context.cropSelection.active)
            return;

        const selection = context.cropSelection;
        if (resize)
        {
            applySelection(selection.x, selection.y, Math.max(1, selection.width + dx), Math.max(1, selection.height + dy));
        }
        else
        {
            selection.x = Math.round(Math.max(0, Math.min(context.canvas.value.width - selection.width, selection.x + dx)));
            selection.y = Math.round(Math.max(0, Math.min(context.canvas.value.height - selection.height, selection.y + dy)));
            updatePreview();
        }
        context.saveHistory(context.translate(resize ? '[[[Zmiana rozmiaru kadru]]]' : '[[[Przesunięcie kadru]]]'));
    }

    function startCrop(event: MouseEvent)
    {
        if (!context.canvas.value)
            return;

        const start = pointFromEvent(event);

        function move(moveEvent: MouseEvent)
        {
            // próba kadrowania w trybie „bez przycinania" włącza tryb dowolny
            if (context.cropMode.value === 'none')
            {
                context.cropMode.value = 'free';
            }

            const current = pointFromEvent(moveEvent);

            applySelection(start.x, start.y, current.x - start.x, current.y - start.y);
        }

        function stop()
        {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            context.saveHistory(context.translate('[[[Kadrowanie obrazu]]]'));
        }

        applySelection(start.x, start.y, 1, 1);
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    }

    function startMove(event: MouseEvent)
    {
        event.stopPropagation();

        if (context.cropMode.value === 'none' || !context.cropSelection.active)
            return;

        const start = pointFromEvent(event);
        const initial = { ...context.cropSelection };

        function move(moveEvent: MouseEvent)
        {
            const current = pointFromEvent(moveEvent);
            const x = Math.max(0, Math.min(context.canvas.value.width - initial.width, initial.x + current.x - start.x));
            const y = Math.max(0, Math.min(context.canvas.value.height - initial.height, initial.y + current.y - start.y));

            context.cropSelection.x = Math.round(x);
            context.cropSelection.y = Math.round(y);
            updatePreview();
        }

        function stop()
        {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            context.saveHistory(context.translate('[[[Przesunięcie kadru]]]'));
        }

        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    }

    function startResize(handle: string, event: MouseEvent)
    {
        event.stopPropagation();

        if (context.cropMode.value === 'none' || !context.cropSelection.active)
            return;

        const initial = { ...context.cropSelection };

        function move(moveEvent: MouseEvent)
        {
            const current = pointFromEvent(moveEvent);
            const x = handle.includes('w') ? initial.x + initial.width : initial.x;
            const y = handle.includes('n') ? initial.y + initial.height : initial.y;

            applySelection(x, y, current.x - x, current.y - y);
        }

        function stop()
        {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
            context.saveHistory(context.translate('[[[Zmiana rozmiaru kadru]]]'));
        }

        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    }

    return {
        setViewportZoom,
        resetSelection,
        redrawCanvas,
        updatePreview,
        setCropMode,
        applyTemplate,
        rotateImage,
        applyTransform,
        nudgeSelection,
        startCrop,
        startMove,
        startResize
    };
}
